import { FMSType, LocationVarKey } from "@swivl-mono/types";

import { IF_ANY, IS_EQUAL, IS_NOT_EQUAL } from "../../../../Components/Worksheets/Types/Worksheet.Interface";
import {
  DropdownRow,
  Indent,
  InputRow,
  PageHeader,
  Section,
  SectionHeader,
  SwitchRow,
  TextAreaRow,
  TextRow
} from "../../../../Components/Worksheets/WorksheetComponent/WorksheetComponents";
import { usePropertiesGetLocationIds } from "../../../../Hooks/usePropertiesGetLocationIds";

export const PAGE_KEYS: LocationVarKey[] = [
  LocationVarKey.VoicePreWelcomeMessage,
  LocationVarKey.VoiceWelcomeMessage,
  LocationVarKey.VoiceDelinquentWelcomeMessage,
  LocationVarKey.VoiceAnythingElseMessage,
  LocationVarKey.VoiceHangupMessage,
  LocationVarKey.VoiceSalesEscalateTo,
  LocationVarKey.VoiceSalesPhoneNumber,
  LocationVarKey.VoiceSalesPhoneNumberExtension,
  LocationVarKey.VoiceServiceEscalateTo,
  LocationVarKey.VoiceServicePhoneNumber,
  LocationVarKey.VoiceServicePhoneNumberExtension,
  LocationVarKey.VoicePermissionToEscalateMessage,
  LocationVarKey.VoicePreEscalateMessage,
  LocationVarKey.LLMVoiceSalesAgentEnabled,
  LocationVarKey.LLMVoiceSupportAgentEnabled,
  LocationVarKey.VoiceHasShowInventory,
  LocationVarKey.VoiceHasAccountBalance,
  LocationVarKey.VoiceHasAccountBalanceIntegration,
  LocationVarKey.VoiceHasAccountNumber,
  LocationVarKey.VoiceHasAccountNumberIntegration,
  LocationVarKey.VoiceHasGateCode,
  LocationVarKey.VoiceHasGateCodeIntegration,
  LocationVarKey.VoiceHasPayBill,
  LocationVarKey.VoiceHasLoginIssues,
  LocationVarKey.VoiceHasCancel,
  LocationVarKey.VoiceHasEditAccountInfo,
  LocationVarKey.VoiceHasGetLease,
  LocationVarKey.VoiceHasPaymentHistory,
  LocationVarKey.VoiceHasAutopay,
  LocationVarKey.VoiceHasManageInsurance
];

export default function WorksheetView() {
  const page = usePropertiesGetLocationIds();
  return (
    <>
      <PageHeader {...page}>General Settings</PageHeader>

      <h4>Pre-Welcome Message</h4>
      <TextAreaRow {...page} marginTop label="" keyName={LocationVarKey.VoicePreWelcomeMessage} />
      <h4>Welcome Message</h4>
      <TextAreaRow {...page} marginTop label="" keyName={LocationVarKey.VoiceWelcomeMessage} />
      <h4>Welcome Message if Delinquent</h4>
      <TextAreaRow {...page} marginTop label="" keyName={LocationVarKey.VoiceDelinquentWelcomeMessage} />
      <h4>Anything Else Message</h4>
      <TextAreaRow {...page} marginTop label=" " keyName={LocationVarKey.VoiceAnythingElseMessage} />
      <h4>Hangup Message</h4>
      <TextAreaRow {...page} marginTop label=" " keyName={LocationVarKey.VoiceHangupMessage} />
      <SectionHeader {...page}> Escalation Settings </SectionHeader>
      <div className="cortexDescription">
        Calls will be forwarded to the direct lines below when a caller needs to speak with your team. If using an external phone number, it should
        ring your staff directly (most likely not your customer-facing numbers, which forward to swivl and could create a loop).
      </div>
      <DropdownRow
        {...page}
        label="Direct Line to Sales Team"
        marginTop
        keyName={LocationVarKey.VoiceSalesEscalateTo}
        options={[
          { label: "swivl Conversations", value: "swivl" },
          { label: "External Phone Number", value: "phone" },
          { label: "None", value: "none" }
        ]}
      />
      <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.VoiceSalesEscalateTo, "none")}>
        <h4>No Sales Agent Available Message</h4>
        <TextAreaRow
          {...page}
          marginTop
          hideIf={IS_NOT_EQUAL(LocationVarKey.VoiceSalesEscalateTo, "none")}
          keyName={LocationVarKey.VoiceNoAgentAvailableSales1}
        />
      </Section>
      <InputRow
        {...page}
        hideIf={IS_NOT_EQUAL(LocationVarKey.VoiceSalesEscalateTo, "phone")}
        keyName={LocationVarKey.VoiceSalesPhoneNumber}
        label="Sales Escalation Phone Number"
      />
      <InputRow
        {...page}
        hideIf={IS_NOT_EQUAL(LocationVarKey.VoiceSalesEscalateTo, "phone")}
        keyName={LocationVarKey.VoiceSalesPhoneNumberExtension}
        label="Sales Extension (if applicable)"
      />

      <DropdownRow
        {...page}
        label="Direct Line to Customer Service Team"
        marginTop
        keyName={LocationVarKey.VoiceServiceEscalateTo}
        options={[
          { label: "swivl Conversations", value: "swivl" },
          { label: "External Phone Number", value: "phone" },
          { label: "None", value: "none" }
        ]}
      />
      <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.VoiceServiceEscalateTo, "none")}>
        <h4>No Customer Service Agent Available Message</h4>
        <TextAreaRow
          {...page}
          marginTop
          hideIf={IS_NOT_EQUAL(LocationVarKey.VoiceServiceEscalateTo, "none")}
          keyName={LocationVarKey.VoiceNoAgentAvailableService1}
        />
      </Section>
      <InputRow
        {...page}
        hideIf={IS_NOT_EQUAL(LocationVarKey.VoiceServiceEscalateTo, "phone")}
        keyName={LocationVarKey.VoiceServicePhoneNumber}
        label="Customer Service Escalation Phone Number"
      />
      <InputRow
        {...page}
        hideIf={IS_NOT_EQUAL(LocationVarKey.VoiceServiceEscalateTo, "phone")}
        keyName={LocationVarKey.VoiceServicePhoneNumberExtension}
        label="Service Extension (if applicable)"
      />

      <h4>Permission to Escalate Message</h4>
      <TextAreaRow {...page} marginTop label=" " keyName={LocationVarKey.VoicePermissionToEscalateMessage} />
      <h4>Pre-Escalate Message</h4>
      <TextAreaRow {...page} marginTop label=" " keyName={LocationVarKey.VoicePreEscalateMessage} />
      <TextRow {...page}>
        {" "}
        Be sure to review your<a href="https://v3.swivl.studio/properties/page/cortex/sequences/sms-settings"> SMS Disclaimer </a> message. Use this
        to introduce callers to your company and provide opt-out instructions.
      </TextRow>

      <SectionHeader {...page} subheading="">
        {" "}
        Use LLM Agents To ...{" "}
      </SectionHeader>
      <SwitchRow {...page} keyName={LocationVarKey.LLMVoiceSalesAgentEnabled} label="Answer inbound calls that relate to sales" />
      <SwitchRow {...page} keyName={LocationVarKey.LLMVoiceSupportAgentEnabled} label="Answer inbound calls that relate to support" />

      {/* <SectionHeader {...page}> Automations </SectionHeader>
      <br></br>
      <SwitchRow {...page} keyName={LocationVarKey.VoiceHasShowInventory} label="Rent a Unit" />
      <SwitchRow {...page} keyName={LocationVarKey.VoiceHasAccountBalance} label="Account Balance" />
      <Indent {...page}>
        <SwitchRow
          {...page}
          keyName={LocationVarKey.VoiceHasAccountBalanceIntegration}
          label="FMS Integration"
          hideIf={IS_NOT_EQUAL(LocationVarKey.VoiceHasAccountBalance, "1")}
        />
      </Indent>
      <SwitchRow
        {...page}
        keyName={LocationVarKey.VoiceHasAccountNumber}
        label="Account Number"
        hideIf={IS_NOT_EQUAL(LocationVarKey.FMSType, FMSType.StorEdge)}
      />
      <Indent {...page}>
        <SwitchRow
          {...page}
          keyName={LocationVarKey.VoiceHasAccountNumberIntegration}
          label="FMS Integration"
          hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.VoiceHasAccountNumber, "1"), IS_NOT_EQUAL(LocationVarKey.FMSType, FMSType.StorEdge)])}
        />
      </Indent>
      <SwitchRow {...page} keyName={LocationVarKey.VoiceHasGateCode} label="Gate Code" />
      <Indent {...page}>
        <SwitchRow
          {...page}
          keyName={LocationVarKey.VoiceHasGateCodeIntegration}
          label="FMS Integration"
          hideIf={IS_NOT_EQUAL(LocationVarKey.VoiceHasGateCode, "1")}
        />
      </Indent>
      <SwitchRow {...page} keyName={LocationVarKey.VoiceHasPayBill} label="Pay Bill" />
      <SwitchRow {...page} keyName={LocationVarKey.VoiceHasLoginIssues} label="Login Issues" />
      <SwitchRow {...page} keyName={LocationVarKey.VoiceHasCancel} label="Cancel" />
      <SwitchRow {...page} keyName={LocationVarKey.VoiceHasEditAccountInfo} label="Update Account Info" />
      <SwitchRow {...page} keyName={LocationVarKey.VoiceHasGetLease} label="Get Lease" />
      <SwitchRow {...page} keyName={LocationVarKey.VoiceHasPaymentHistory} label="Payment History" />
      <SwitchRow {...page} keyName={LocationVarKey.VoiceHasAutopay} label="Autopay" />
      <SwitchRow {...page} keyName={LocationVarKey.VoiceHasManageInsurance} label="Manage Insurance" /> */}
    </>
  );
}
